import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Cast, Loader2, PlugZap, Trash2, Eye, EyeOff, Save, Plus, Pencil, Power, KeyRound, Sparkles, History, Radio, CheckCircle2, AlertTriangle, Copy, Server, ShieldAlert } from "lucide-react";
import { ObsIngestDialog } from "./cdn/ObsIngestDialog";
import { DestinationDialog } from "./cdn/DestinationDialog";

const STATUS_CLS = {
  pushing: "bg-emerald-500/15 text-emerald-500 border-emerald-400/40",
  "pushing-vod": "bg-cyan-500/15 text-cyan-400 border-cyan-400/40",
  "pushing-category": "bg-violet-500/15 text-violet-400 border-violet-400/40",
  "pushing-demo": "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-400/40",
  rotating: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-400/40",
  switching: "bg-cyan-500/15 text-cyan-400 border-cyan-400/40",
  connected: "bg-emerald-500/15 text-emerald-500 border-emerald-400/40",
  reachable: "bg-emerald-500/15 text-emerald-500 border-emerald-400/40",
  starting: "bg-amber-500/15 text-amber-500 border-amber-400/40",
  connecting: "bg-amber-500/15 text-amber-500 border-amber-400/40",
  restarting: "bg-amber-500/15 text-amber-500 border-amber-400/40",
  "input-offline": "bg-amber-500/15 text-amber-500 border-amber-400/40",
  "requires-lan-probe": "bg-amber-500/15 text-amber-500 border-amber-400/40",
  error: "bg-rose-500/15 text-rose-500 border-rose-400/40",
  stopped: "bg-muted text-muted-foreground border-border",
};

const STATUS_LABELS = {
  "pushing-vod": "pushing · vod",
  "pushing-category": "pushing · category",
  "pushing-demo": "pushing · demo",
  rotating: "rotating slot",
  switching: "switching to live",
  connecting: "connecting",
};

function leaseCountdown(expiresAt) {
  try {
    const secs = Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000);
    if (secs <= 0) return "expired";
    return `${secs}s`;
  } catch { return "?"; }
}

function fmtDrillTime(iso) {
  try {
    const d = new Date(iso);
    const secs = Math.round((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return "just now";
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}


function RelayHealthStrip({ channelId, dest }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!channelId || !dest?.id) return undefined;
    let cancel = false;
    const load = () =>
      api.get(`/channels/${channelId}/cdn-destinations/${dest.id}/relay-health`)
        .then((r) => { if (!cancel) setH(r.data); })
        .catch(() => { /* noop */ });
    load();
    const t = setInterval(load, 3000);
    return () => { cancel = true; clearInterval(t); };
  }, [channelId, dest?.id]);

  const fmtUp = (s) => {
    if (!s && s !== 0) return "—";
    const h2 = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
    return h2 ? `${h2}h ${m}m` : m ? `${m}m ${sec}s` : `${sec}s`;
  };
  const fmtBytes = (b) => {
    if (!b) return "0 MB";
    const mb = b / 1e6;
    return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
  };
  const active = h?.active && h?.relay_alive;
  const stale = !!h?.stale;
  const stalled = h?.active && h?.seconds_since_progress != null && h.seconds_since_progress > 10;
  const tone = stale ? "text-amber-400" : !h?.active ? "text-muted-foreground" : stalled ? "text-rose-400" : "text-emerald-400";
  const stateTxt = stale ? "stale" : !h?.active ? "idle" : stalled ? "stalled" : active ? "live" : "reconnecting";
  const inst = h?.instance_id || null;
  const shortInst = inst ? (inst.length > 14 ? `…${inst.slice(-13)}` : inst) : null;
  const Metric = ({ label, value, testid }) => (
    <div className="flex flex-col">
      <span className="text-[8px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-[11px] font-mono font-bold text-foreground" data-testid={testid}>{value}</span>
    </div>
  );
  return (
    <div className="px-3 pb-2 pt-0" data-testid={`relay-health-${dest.id}`}>
      <div className="rounded-sm border border-violet-500/25 bg-violet-500/5 px-3 py-2">
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <span className="text-[9px] font-mono uppercase tracking-widest text-violet-300/80">Relay split · health</span>
          <div className="flex items-center gap-2">
            {inst && (
              <span className="text-[9px] font-mono text-violet-300/70 inline-flex items-center gap-1" title={`Serving instance: ${inst}`} data-testid={`relay-instance-${dest.id}`}>
                <Server className="w-2.5 h-2.5" /> {shortInst}
              </span>
            )}
            {stale && (
              <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-400/30"
                title={`Last health update ${h?.age_sec != null ? Math.round(h.age_sec) + "s" : ">20s"} ago — snapshot is stale`}
                data-testid={`relay-stale-${dest.id}`}>
                stale {h?.age_sec != null ? `${Math.round(h.age_sec)}s` : ""}
              </span>
            )}
            <span className={`text-[10px] font-mono font-bold ${tone}`} data-testid={`relay-health-state-${dest.id}`}>
              {stateTxt}
            </span>
          </div>
        </div>
        {h?.active ? (
          <div className="grid grid-cols-4 gap-3">
            <Metric label="Uptime" value={fmtUp(h.uptime_sec)} testid={`relay-uptime-${dest.id}`} />
            <Metric label="Playlist restarts" value={h.playlist_restarts ?? 0} testid={`relay-restarts-${dest.id}`} />
            <Metric label="Relay reconnects" value={h.relay_respawns ?? 0} testid={`relay-reconnects-${dest.id}`} />
            <Metric label="Pushed" value={`${fmtBytes(h.bytes_pushed)} · ${h.mbps ?? 0} Mbps`} testid={`relay-throughput-${dest.id}`} />
          </div>
        ) : (
          <div className="text-[10px] font-mono text-muted-foreground">
            {stale
              ? `Relay health is stale — the serving instance${inst ? ` (${shortInst})` : ""} stopped reporting. Failover should pick it up shortly.`
              : "Relay not running — enable the push with relay-split on to see live metrics."}
          </div>
        )}
      </div>
    </div>
  );
}


function PushHealthStrip({ channelId, dest }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!channelId || !dest?.id) return;
    let cancel = false;
    const load = () =>
      api.get(`/channels/${channelId}/cdn-destinations/${dest.id}/health-history?hours=24`)
        .then((r) => { if (!cancel) setH(r.data); })
        .catch(() => { /* noop */ });
    load();
    const t = setInterval(load, 30000);
    return () => { cancel = true; clearInterval(t); };
  }, [channelId, dest?.id]);

  const segs = h?.segments || [];
  const up = h?.uptime_pct ?? 0;
  const tone = up >= 99 ? "text-emerald-400" : up >= 90 ? "text-amber-400" : "text-rose-400";
  return (
    <div className="px-3 pb-2 pt-0" data-testid={`cdn-health-${dest.id}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Push uptime · last 24h</span>
        <div className="flex items-center gap-2">
          {segs.length > 0 && (h?.flaps > 0 ? (
            <span
              className="text-[9px] font-mono uppercase tracking-widest text-amber-400"
              title={`${h.flaps} drop(s) · ${Math.round((h.downtime_sec || 0) / 60)}m total downtime in the last 24h`}
              data-testid={`cdn-health-flaps-${dest.id}`}
            >
              {h.flaps} flap{h.flaps === 1 ? "" : "s"} · {Math.max(1, Math.round((h.downtime_sec || 0) / 60))}m down
            </span>
          ) : (
            <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-500/70" data-testid={`cdn-health-flaps-${dest.id}`}>
              stable
            </span>
          ))}
          <span className={`text-[10px] font-mono font-bold ${segs.length ? tone : "text-muted-foreground"}`} data-testid={`cdn-health-uptime-${dest.id}`}>
            {segs.length ? `${up}% up` : "no history yet"}
          </span>
        </div>
      </div>
      <div className="relative h-2 rounded-full bg-zinc-800 overflow-hidden">
        {segs.map((s, i) => (
          <div
            key={i}
            title={`${s.status} · ${Math.max(1, Math.round(s.duration_sec / 60))}m`}
            className={`absolute top-0 h-full ${
              s.healthy ? "bg-emerald-500/80"
                : (s.status || "").includes("error") ? "bg-rose-500/70"
                : "bg-amber-500/60"
            }`}
            style={{ left: `${s.start_pct}%`, width: `${Math.max(0.4, s.width_pct)}%` }}
            data-testid={`cdn-health-seg-${dest.id}-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function CustomCdnPanel({ channel }) {
  const [dests, setDests] = useState([]);
  const [conflicts, setConflicts] = useState({});
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(null); // null | 'new' | destObj
  const [authOpen, setAuthOpen] = useState(null); // null | destObj
  const [repairing, setRepairing] = useState(false);
  const [pushAlerts, setPushAlerts] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [testPush, setTestPush] = useState(null); // null | {dest, loading, result}
  const [obsOpen, setObsOpen] = useState(null); // null | destObj
  const seenAlertRef = React.useRef(new Set());

  const load = async () => {
    if (!channel?.id) return;
    setBusy(true);
    try {
      const r = await api.get(`/channels/${channel.id}/cdn-destinations`);
      setDests(r.data || []);
      api.get(`/channels/${channel.id}/cdn-destinations/conflicts`)
        .then((c) => setConflicts(c.data?.conflicts || {}))
        .catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [channel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll status of any active destination every 5 s
  useEffect(() => {
    if (!dests.length || !channel?.id) return;
    const t = setInterval(async () => {
      const active = dests.filter((d) => d.enabled);
      if (!active.length) return;
      const updates = await Promise.all(
        active.map((d) =>
          api.get(`/channels/${channel.id}/cdn-destinations/${d.id}/status`)
            .then((r) => ({ id: d.id, ...r.data }))
            .catch(() => null)
        )
      );
      setDests((cur) => cur.map((d) => {
        const u = updates.find((x) => x && x.id === d.id);
        return u ? {
          ...d,
          last_status: u.persisted_status,
          last_error: u.last_error,
          last_actionable_error: u.last_actionable_error,
          last_actionable_error_at: u.last_actionable_error_at,
          now_source: u.now_source,
          now_asset_name: u.now_asset_name,
          now_category_name: u.now_category_name,
          active_target: u.active_target,
          failover_at: u.failover_at,
          failover_enabled: u.failover_enabled,
          failover_drill: u.failover_drill,
          last_drill_at: u.last_drill_at,
          lease: u.lease,
        } : d;
      }));
    }, 5000);
    return () => clearInterval(t);
  }, [dests, channel?.id]);

  // Push watchdog — poll active push-error alerts for this channel, toast new
  useEffect(() => {
    if (!channel?.id) return;
    let cancel = false;
    const load = () =>
      api.get(`/alerts/push-errors?active_only=true`)
        .then((r) => {
          if (cancel) return;
          const mine = (r.data?.items || []).filter((a) => a.channel_id === channel.id);
          setPushAlerts(mine);
          mine.forEach((a) => {
            const key = a.destination_id + "|" + a.error_since;
            if (!seenAlertRef.current.has(key)) {
              seenAlertRef.current.add(key);
              toast.error(`Push failing · ${a.destination_name}`, { description: a.error?.slice(0, 140) });
            }
          });
        })
        .catch(() => { /* noop */ });
    load();
    const t = setInterval(load, 15000);
    return () => { cancel = true; clearInterval(t); };
  }, [channel?.id]);

  const toggle = async (dest) => {
    try {
      const r = await api.patch(`/channels/${channel.id}/cdn-destinations/${dest.id}`, {
        enabled: !dest.enabled,
      });
      setDests((p) => p.map((d) => (d.id === dest.id ? r.data : d)));
      toast.success(`${dest.name} · ${r.data.enabled ? "ENABLED" : "DISABLED"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  const setRung = async (dest, preset) => {
    try {
      const r = await api.patch(`/channels/${channel.id}/cdn-destinations/${dest.id}`, {
        encode_preset: preset,
      });
      setDests((p) => p.map((d) => (d.id === dest.id ? r.data : d)));
      toast.success(`${dest.name} · broadcast ${preset === "1080p25" ? "1080p" : "720p"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to set rung"); }
  };

  const remove = async (dest) => {
    if (!window.confirm(`Delete destination "${dest.name}"?`)) return;
    try {
      await api.delete(`/channels/${channel.id}/cdn-destinations/${dest.id}`);
      setDests((p) => p.filter((d) => d.id !== dest.id));
      toast.success(`Removed ${dest.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const runTest = async (dest) => {
    try {
      const r = await api.post(`/channels/${channel.id}/cdn-destinations/${dest.id}/test`);
      const cls = STATUS_CLS[r.data.status] || STATUS_CLS.stopped;
      toast(`${r.data.status.toUpperCase()} · ${r.data.detail}`, { className: cls });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
  };

  const runTestPush = async (dest) => {
    if (!window.confirm(
      `Run a real ~10s test publish to "${dest.name}"?\n\nThis pushes a synthetic signal to the actual endpoint. If this destination accepts only one publisher at a time (e.g. Flussonic push2stream), it may briefly interrupt a live push to the same stream key.`
    )) return;
    setTestPush({ dest, loading: true, result: null });
    try {
      const r = await api.post(`/channels/${channel.id}/cdn-destinations/${dest.id}/test-push`);
      setTestPush({ dest, loading: false, result: r.data });
      if (r.data.ok) toast.success(`Publish accepted · sustained ${r.data.uptime_s}s`);
      else if (r.data.busy) toast("Destination is live — test skipped", { description: "Stop the push to run an isolated test." });
      else toast.error(r.data.supported === false ? "Not push-testable" : "Publish rejected — see details");
    } catch (e) {
      setTestPush({ dest, loading: false, result: { ok: false, hint: e?.response?.data?.detail || "Test push failed", tail: [] } });
      toast.error(e?.response?.data?.detail || "Test push failed");
    }
  };

  const runFailoverDrill = async (dest) => {
    try {
      const r = await api.post(`/channels/${channel.id}/cdn-destinations/${dest.id}/failover-drill`);
      // Optimistically flip the badge; the 5s poll keeps it in sync + recovers it.
      setDests((p) => p.map((d) => (d.id === dest.id ? { ...d, active_target: "backup", failover_drill: true, last_drill_at: new Date().toISOString() } : d)));
      toast(r.data?.message || "Failover drill started", { className: STATUS_CLS["pushing-category"] });
      // Safety net: disabled destinations aren't polled, so auto-clear the
      // optimistic flip once the backend drill window (+buffer) has elapsed.
      const clearAfter = ((r.data?.hold_sec || 6) + 2) * 1000;
      setTimeout(() => {
        setDests((p) => p.map((d) => (
          d.id === dest.id && d.failover_drill
            ? { ...d, active_target: "primary", failover_drill: false }
            : d)));
      }, clearAfter);
    } catch (e) { toast.error(e?.response?.data?.detail || "Drill failed"); }
  };

  const repairCoverage = async () => {
    if (!channel?.id) return;
    if (!window.confirm(
      "Fill every scheduled category slot on this channel that currently has NO CDN-ready clips with the full ready-asset pool?\n\nThis stops the live push from falling back to the demo test pattern. Curated categories that already have content are left untouched."
    )) return;
    setRepairing(true);
    try {
      const r = await api.post(`/channels/${channel.id}/repair-content-coverage`, { mode: "empty_only" });
      const d = r.data || {};
      if (d.note) {
        toast(d.note);
      } else {
        toast.success(
          `Coverage repaired · ${d.categories_updated} categor${d.categories_updated === 1 ? "y" : "ies"} filled · ` +
          `empty slots ${d.empty_before}→${d.empty_after} · ${d.ready_assets} ready clips`
        );
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
    setRepairing(false);
  };

  return (
    <section className="panel p-4 space-y-3" data-testid="custom-cdn-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cast className="w-4 h-4 text-fuchsia-400" />
          <div>
            <div className="font-display font-bold text-sm">Custom / External CDN destinations</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              push this channel simultaneously to N outputs · Twitch · YouTube · Facebook · Flussonic · SRT
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            className="rounded-sm h-8 text-xs border-border text-muted-foreground"
            onClick={() => setHistoryOpen(true)}
            title="Past push-error incidents (duration + resolution)"
            data-testid="cdn-incident-history-btn"
          >
            <History className="w-3.5 h-3.5 mr-1" /> History
          </Button>
          <Button
            size="sm" variant="outline"
            className="rounded-sm h-8 text-xs border-cyan-500/40 text-cyan-400"
            onClick={repairCoverage}
            disabled={repairing}
            title="Fill scheduled category slots that have no CDN-ready clips so the push never airs the demo test pattern"
            data-testid="cdn-repair-coverage"
          >
            {repairing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Fill empty slots
          </Button>
          <Button size="sm" className="rounded-sm h-8 text-xs tenant-btn" onClick={() => setEditOpen("new")} data-testid="cdn-add-dest">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add destination
          </Button>
        </div>
      </div>

      {pushAlerts.length > 0 && (
        <div
          className="border border-rose-500/40 bg-rose-500/10 rounded-sm px-3 py-2 space-y-1"
          data-testid="cdn-push-alert-banner"
        >
          <div className="flex items-center gap-2 text-rose-400 text-xs font-display font-bold">
            <PlugZap className="w-3.5 h-3.5" /> Push failing &gt; 60s — action needed
          </div>
          {pushAlerts.map((a) => (
            <div key={a.destination_id + a.error_since} className="text-[11px] font-mono text-rose-300/90 leading-snug" data-testid={`cdn-push-alert-${a.destination_id}`}>
              <span className="font-bold">{a.destination_name}</span>: {a.error}
            </div>
          ))}
        </div>
      )}

      <div className="border border-border rounded-sm overflow-x-auto min-w-0">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Protocol</th>
              <th className="text-left px-3 py-2">Endpoint</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {dests.map((d) => {
              const cls = d.last_status ? (STATUS_CLS[d.last_status] || STATUS_CLS.stopped) : STATUS_CLS.stopped;
              const isPush = ["rtmp", "rtmps", "srt", "hls_push"].includes(d.protocol);
              return (
                <React.Fragment key={d.id}>
                <tr className={`border-t border-border ${!d.enabled ? "opacity-50" : ""}`} data-testid={`cdn-dest-${d.id}`}>
                  <td className="px-3 py-2 font-display font-bold">{d.name}</td>
                  <td className="px-3 py-2 text-xs font-mono uppercase">
                    <div>{d.protocol}</div>
                    <div className="text-[9px] normal-case text-muted-foreground mt-0.5" data-testid={`cdn-dest-preset-${d.id}`}>
                      {(() => {
                        const isCopy = !d.encode_preset || d.encode_preset === "copy";
                        const isRtmp = ["rtmp", "rtmps"].includes(d.protocol);
                        const encoding = isRtmp || !isCopy;
                        const effPreset = isCopy ? (isRtmp ? "720p25" : "copy") : d.encode_preset;
                        const presetLabel = isCopy && isRtmp ? "720p25 (auto)" : effPreset;
                        if (!encoding) return <span>copy</span>;
                        const vbr = d.video_bitrate_kbps || PRESET_VBR[effPreset];
                        const abr = d.audio_bitrate_kbps || 128;
                        const tgt = d.loudnorm_target || -23;
                        return (
                          <>
                            <span>{presetLabel}</span>
                            {vbr ? <span className="text-cyan-400/80"> · {fmtBitrate(vbr)}/{abr}k</span> : null}
                            {d.loudnorm !== false
                              ? <span className="text-emerald-500/80" data-testid={`cdn-dest-lufs-${d.id}`} title="EBU R128 loudness target"> · −{Math.abs(tgt)} LUFS</span>
                              : <span className="text-amber-500/80" data-testid={`cdn-dest-lufs-${d.id}`} title="Loudness normalisation off — source levels passed through"> · src audio</span>}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs font-mono truncate max-w-[240px]" title={d.endpoint}>{d.endpoint}</td>
                  <td className="px-3 py-2">
                    <Badge
                      className={`${cls} border text-[9px] font-mono uppercase`}
                      title={d.last_error || ""}
                      data-testid={`cdn-dest-status-${d.id}`}
                    >
                      {STATUS_LABELS[d.last_status] || d.last_status || (d.enabled ? "pending" : "off")}
                    </Badge>
                    {d.enabled && (d.last_status || "").startsWith("pushing") && (d.now_asset_name || d.now_category_name || d.now_source) && (
                      <div className="text-[10px] font-mono text-emerald-400/90 mt-1 truncate max-w-[240px]" data-testid={`cdn-dest-now-${d.id}`}
                        title={d.now_category_name && d.now_asset_name ? `${d.now_category_name} → ${d.now_asset_name}` : (d.now_asset_name || d.now_source)}>
                        <span className="uppercase tracking-widest text-[9px] text-emerald-500/70">Now · </span>
                        {d.now_category_name && d.now_asset_name
                          ? `${d.now_category_name} → ${d.now_asset_name}`
                          : (d.now_asset_name || (d.now_source === "demo" ? "demo test pattern" : d.now_source || ""))}
                      </div>
                    )}
                    {d.active_target === "backup" && (
                      <div className={`text-[10px] font-mono mt-0.5 truncate max-w-[240px] flex items-center gap-1 ${d.failover_drill ? "text-violet-400" : "text-amber-400"}`}
                        title={`${d.failover_drill ? "DRILL — live stream untouched" : "Failed over to backup target"}${d.failover_at ? ` at ${d.failover_at}` : ""}`}
                        data-testid={`cdn-dest-failover-${d.id}`}>
                        <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
                        <span className="uppercase tracking-widest text-[9px]">{d.failover_drill ? "ON BACKUP · drill" : "ON BACKUP · failed over"}</span>
                      </div>
                    )}
                    {d.failover_enabled && d.active_target !== "backup" && (
                      <div className="text-[9px] font-mono text-emerald-500/70 mt-0.5 uppercase tracking-widest"
                        data-testid={`cdn-dest-failover-armed-${d.id}`} title="Auto-failover armed — backup target on standby">
                        {(d.last_status || "").startsWith("pushing") ? "failover armed" : "failover armed (idle)"}
                      </div>
                    )}
                    {d.failover_enabled && d.last_drill_at && d.active_target !== "backup" && (
                      <div className="text-[9px] font-mono text-violet-400/70 mt-0.5"
                        data-testid={`cdn-dest-last-drill-${d.id}`}
                        title={`Last failover drill: ${new Date(d.last_drill_at).toLocaleString()}`}>
                        last drill · {fmtDrillTime(d.last_drill_at)} ✓
                      </div>
                    )}
                    {d.lease?.held && d.lease?.instance && (
                      <div className="text-[10px] font-mono text-sky-400/80 mt-0.5 truncate max-w-[240px]"
                        title={`Push owned by instance ${d.lease.instance}${d.lease.is_self ? " (this replica)" : ""} · fencing token ${d.lease.token ?? "?"}${d.lease.expires_at ? ` · lease expires ${d.lease.expires_at}` : ""}`}
                        data-testid={`cdn-dest-lease-${d.id}`}>
                        <span className="uppercase tracking-widest text-[9px] text-sky-500/70">Pushed by · </span>
                        {d.lease.instance.length > 16 ? `${d.lease.instance.slice(0, 16)}…` : d.lease.instance}
                        {d.lease.is_self ? " (this)" : ""}
                        {d.lease.token != null && (
                          <span className="text-sky-300/70" data-testid={`cdn-dest-lease-token-${d.id}`}> · tok {d.lease.token}</span>
                        )}
                        {d.lease.expires_at && (
                          <span className="text-sky-300/50"> · exp {leaseCountdown(d.lease.expires_at)}</span>
                        )}
                      </div>
                    )}
                    {d.last_status === "pushing-vod" && d.last_error && (
                      <div className="text-[9px] font-mono text-cyan-400/80 mt-0.5 truncate max-w-[220px]" title={d.last_error}>
                        {d.last_error.split(" · ")[0]}
                      </div>
                    )}
                    {/* Sticky operator-facing diagnostic — set by the
                        pusher when it detects a known-fatal failure
                        (missing Flussonic creds, DNS failure, etc.) and
                        auto-cleared after a sustained 30 s of healthy
                        pushing. Distinct amber tone so it's obvious. */}
                    {d.last_actionable_error && (
                      <div
                        data-testid={`cdn-dest-diag-${d.id}`}
                        className="text-[10px] font-mono text-amber-400 mt-1 max-w-[380px] leading-snug border-l-2 border-amber-500/40 pl-2"
                        title={d.last_actionable_error}
                      >
                        <span className="uppercase tracking-widest text-[9px] text-amber-500">Action needed · </span>
                        {d.last_actionable_error}
                      </div>
                    )}
                    {(conflicts[d.id] || []).map((w, i) => (
                      <div
                        key={i}
                        data-testid={`cdn-dest-conflict-${d.id}`}
                        className="text-[10px] font-mono text-rose-400 mt-1 max-w-[380px] leading-snug border-l-2 border-rose-500/50 pl-2"
                        title={w}
                      >
                        <span className="uppercase tracking-widest text-[9px] text-rose-500">Double-publish risk · </span>
                        {w}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      {["rtmp", "rtmps", "srt"].includes(d.protocol) && (
                        <div className="flex rounded-sm overflow-hidden border border-border mr-1" data-testid={`cdn-rung-${d.id}`} title="Broadcast rung (H.264 25fps)">
                          {[["720p25", "720p"], ["1080p25", "1080p"]].map(([p, label]) => (
                            <button
                              key={p}
                              onClick={() => setRung(d, p)}
                              data-testid={`cdn-rung-${p}-${d.id}`}
                              className={`px-2 h-7 text-[10px] font-mono uppercase tracking-widest ${
                                (d.encode_preset || "720p25") === p ? "bg-emerald-500/15 text-emerald-400" : "text-muted-foreground hover:bg-muted/40"
                              }`}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="outline" size="sm"
                        className={`rounded-sm h-7 text-xs ${d.enabled ? "border-fuchsia-500/40 text-fuchsia-500" : "border-border"}`}
                        onClick={() => toggle(d)}
                        data-testid={`cdn-toggle-${d.id}`}
                      >
                        <Power className="w-3 h-3 mr-1" /> {d.enabled ? "On" : "Off"}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" onClick={() => setEditOpen(d)} data-testid={`cdn-edit-${d.id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {["rtmp", "rtmps", "srt", "hls_push"].includes(d.protocol) && (
                        <Button
                          variant="outline" size="sm"
                          className={`rounded-sm h-7 text-xs ${d.last_actionable_error ? "border-amber-500/60 text-amber-500" : "border-cyan-500/40 text-cyan-400"}`}
                          onClick={() => setAuthOpen(d)}
                          title="Refresh login / password (Flussonic push2stream)"
                          data-testid={`cdn-auth-${d.id}`}
                        >
                          <KeyRound className="w-3 h-3" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs" onClick={() => runTest(d)} data-testid={`cdn-test-${d.id}`} title="Connectivity probe (TCP)">
                        <PlugZap className="w-3 h-3" />
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-cyan-500/40 text-cyan-500" onClick={() => runTestPush(d)} data-testid={`cdn-test-push-${d.id}`} title="Test Push (10s) — real publish, shows the endpoint's actual accept/reject">
                        <Radio className="w-3 h-3" />
                      </Button>
                      {["rtmp", "rtmps", "srt"].includes(d.protocol) && (
                        <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-sky-500/40 text-sky-400" onClick={() => setObsOpen(d)} data-testid={`cdn-obs-${d.id}`} title="OBS / encoder ingest settings — copy server URL & stream key">
                          <Server className="w-3 h-3" />
                        </Button>
                      )}
                      {d.failover_enabled && (d.backup_endpoint || "").length > 0 && (
                        <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-violet-500/40 text-violet-400" onClick={() => runFailoverDrill(d)} data-testid={`cdn-drill-${d.id}`} title="Failover drill — dry-run rehearsal, live stream untouched">
                          <ShieldAlert className="w-3 h-3" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs border-rose-500/40 text-rose-500" onClick={() => remove(d)} data-testid={`cdn-del-${d.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
                {isPush && (
                  <tr className={`border-0 ${!d.enabled ? "opacity-50" : ""}`}>
                    <td colSpan={5} className="px-0 pt-0 pb-0">
                      <PushHealthStrip channelId={channel?.id} dest={d} />
                    </td>
                  </tr>
                )}
                {isPush && d.relay_split_enabled && (
                  <tr className={`border-0 ${!d.enabled ? "opacity-50" : ""}`}>
                    <td colSpan={5} className="px-0 pt-0 pb-0">
                      <RelayHealthStrip channelId={channel?.id} dest={d} />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {dests.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-xs text-muted-foreground">
                {busy ? "Loading…" : "No destinations yet · click Add destination to push to Twitch / YouTube / Facebook / Flussonic / etc."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <DestinationDialog
        open={!!editOpen}
        dest={editOpen && editOpen !== "new" ? editOpen : null}
        channelId={channel?.id}
        onClose={() => setEditOpen(null)}
        onSaved={(saved, isNew) => {
          setDests((p) => isNew ? [...p, saved] : p.map((d) => (d.id === saved.id ? saved : d)));
          setEditOpen(null);
        }}
      />

      <FlussonicAuthDialog
        open={!!authOpen}
        dest={authOpen}
        channelId={channel?.id}
        onClose={() => setAuthOpen(null)}
        onSaved={(saved) => {
          setDests((p) => p.map((d) => (d.id === saved.id ? saved : d)));
          setAuthOpen(null);
        }}
      />

      <ObsIngestDialog open={!!obsOpen} dest={obsOpen} onClose={() => setObsOpen(null)} />

      <PushIncidentHistoryDialog
        open={historyOpen}
        channelId={channel?.id}
        onClose={() => setHistoryOpen(false)}
      />

      <TestPushDialog
        state={testPush}
        onClose={() => setTestPush(null)}
      />
    </section>
  );
}

function TestPushDialog({ state, onClose }) {
  const open = !!state;
  const { dest, loading, result } = state || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="cdn-test-push-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Radio className="w-4 h-4 text-cyan-400" /> Test Push · {dest?.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Runs a ~10s synthetic publish to this destination and reports whether the endpoint actually accepts the stream (not just whether the port is open).
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="cdn-test-push-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Publishing test signal… (up to ~15s)
          </div>
        )}

        {!loading && result && (
          <div className="space-y-3" data-testid="cdn-test-push-result">
            <div className={`flex items-center gap-2 text-sm font-medium ${result.ok ? "text-emerald-400" : result.busy ? "text-cyan-400" : "text-rose-400"}`}>
              {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {result.ok
                ? `Publish accepted — sustained ${result.uptime_s}s`
                : result.busy ? "Destination is live right now — test skipped"
                : result.supported === false ? "This protocol is not push-testable" : "Publish rejected by the endpoint"}
            </div>

            {(result.exit_code !== undefined && result.exit_code !== null) && (
              <div className="flex flex-wrap gap-4 font-mono text-[11px] text-muted-foreground">
                <span>exit <span className="text-foreground">{result.exit_code}</span></span>
                <span>sustained <span className="text-foreground">{result.uptime_s}s</span> / {result.duration_s}s</span>
                <span>proto <span className="text-foreground">{result.protocol}</span></span>
              </div>
            )}

            {result.url && (
              <div className="font-mono text-[10px] text-muted-foreground break-all">{result.url}</div>
            )}

            {result.hint && (
              <div className={`text-xs rounded-sm border px-3 py-2 ${result.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : result.busy ? "border-cyan-500/30 bg-cyan-500/5 text-cyan-300" : "border-amber-500/30 bg-amber-500/5 text-amber-300"}`} data-testid="cdn-test-push-hint">
                {result.hint}
              </div>
            )}

            {result.tail && result.tail.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">ffmpeg output</div>
                <pre className="text-[10px] font-mono leading-snug bg-black/40 border border-border rounded-sm p-2 max-h-64 overflow-auto whitespace-pre-wrap" data-testid="cdn-test-push-tail">{result.tail.join("\n")}</pre>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={onClose} data-testid="cdn-test-push-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function _fmtDur(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`;
}

function PushIncidentHistoryDialog({ open, channelId, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !channelId) return;
    setBusy(true);
    api.get(`/alerts/push-errors?active_only=false`)
      .then((r) => setRows((r.data?.items || []).filter((a) => a.channel_id === channelId)))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  }, [open, channelId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="cdn-incident-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="w-4 h-4 text-cyan-400" /> Push incident history
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Destinations that stayed in <span className="font-mono">error</span> for over 60s. Duration is measured from first error to recovery.
          </DialogDescription>
        </DialogHeader>
        <div className="border border-border rounded-sm overflow-x-auto max-h-[50vh]">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Destination</th>
                <th className="text-left px-3 py-2">Started</th>
                <th className="text-left px-3 py-2">Duration</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={i} className="border-t border-border" data-testid={`cdn-incident-row-${i}`}>
                  <td className="px-3 py-2 font-display font-bold">{a.destination_name}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">
                    {a.error_since ? new Date(a.error_since).toLocaleString() : (a.at ? new Date(a.at).toLocaleString() : "—")}
                  </td>
                  <td className="px-3 py-2 text-[11px] font-mono">{_fmtDur(a.duration_sec)}</td>
                  <td className="px-3 py-2">
                    <Badge className={`border text-[9px] font-mono uppercase ${a.open ? "bg-rose-500/15 text-rose-400 border-rose-400/40" : "bg-emerald-500/15 text-emerald-500 border-emerald-400/40"}`}>
                      {a.open ? "active" : "resolved"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground truncate max-w-[260px]" title={a.error}>{a.error}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-xs text-muted-foreground" data-testid="cdn-incident-empty">
                  {busy ? "Loading…" : "No push-error incidents recorded — all destinations healthy."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FlussonicAuthDialog({ open, dest, channelId, onClose, onSaved }) {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && dest) {
      setLogin(dest.username || "");
      setPassword(dest.password || "");
      setShowPw(false);
    }
  }, [open, dest]);

  const save = async () => {
    if (!login.trim()) { toast.error("Login is required for Flussonic push2stream"); return; }
    setBusy(true);
    try {
      const r = await api.patch(`/channels/${channelId}/cdn-destinations/${dest.id}`, {
        username: login.trim(),
        password: password.trim() || null,
      });
      toast.success("Auth refreshed — push restarting with new credentials");
      onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="cdn-auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-cyan-400" /> Refresh Flussonic auth
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Update the login + password for <span className="text-foreground font-bold">{dest?.name}</span>. Saving re-appends
            <span className="font-mono"> ?login=…&amp;password=… </span> and restarts the push immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {dest?.last_actionable_error && (
            <div className="text-[11px] font-mono text-amber-400 border-l-2 border-amber-500/40 pl-2 leading-snug" data-testid="cdn-auth-diag">
              {dest.last_actionable_error}
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Login</Label>
            <Input value={login} onChange={(e) => setLogin(e.target.value)} className="mt-1 rounded-sm font-mono text-xs" placeholder="flussonic-login" data-testid="cdn-auth-login" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Password</Label>
            <div className="mt-1 flex gap-1">
              <Input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm font-mono text-xs" data-testid="cdn-auth-password" />
              <Button variant="outline" size="sm" className="rounded-sm h-9 px-2" onClick={() => setShowPw((v) => !v)}>
                {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="rounded-sm h-8 text-xs tenant-btn" onClick={save} disabled={busy} data-testid="cdn-auth-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1" />} Save &amp; restart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Mirror of backend ENCODE_PRESETS video bitrate defaults (kbps) so the row
// badge can show the effective bitrate without opening the edit form.
const PRESET_VBR = {
  "480p30": 1200, "720p25": 2500, "720p30": 2500, "720p60": 4500,
  "1080p25": 5000, "1080p30": 4500, "1080p60": 6000,
};

function fmtBitrate(kbps) {
  if (!kbps) return "";
  return kbps >= 1000 ? `${kbps % 1000 === 0 ? kbps / 1000 : (kbps / 1000).toFixed(1)}M` : `${kbps}k`;
}
